import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi,type GridReadyEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, FormControlLabel, Input, MenuItem, Radio, RadioGroup, Select } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import plusIcon from "@user/assets/images/common/plus-secondary.svg";
import minusIcon from "@user/assets/images/common/minus-secondary.svg";
import messageIcon from "@user/assets/images/common/message-info.svg";

import "./popup_sku_req.scoped.scss";

interface State {
  gridApi: GridApi | null,
  skuWbCellQtyCodes: Code[]
}

const defState: State = {
  gridApi: null,
  skuWbCellQtyCodes: []
};

interface SearchParams {
  workStationId: string,
  skuReqType: string,
  sortOrder: string,
  procType: string,
  skuType: string,
  skuSize: string,
  skuCellQty: string,
  skuQty: string
}

const defSearchParams = {
  workStationId: "",
  skuReqType: "R",
  sortOrder: "stockRateAsc_random",
  procType: "A",
  skuType: "WB",
  skuSize: "SS",
  skuCellQty: "4",
  skuQty: "1"
};

interface Result {
  searchParams: SearchParams,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  infos: []
};

const validationMsg4range = [
  {id: "workStationId", type: "NotBlank", msg: "작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."},
  {id: "skuReqType", type: "NotBlank", msg: "호출구분을 선택해 주세요."},
  {id: "skuReqType", type: "InvalidType", msg: "호출구분을 확인해 주세요."},
  {id: "sortOrder", type: "NotBlank", msg: "적재가능률을 선택해 주세요."},
  {id: "skuType", type: "NotBlank", msg: "SKU Type을 선택해 주세요."},
  {id: "skuSize", type: "NotBlank", msg: "SKU Size를 선택해 주세요."},
  {id: "skuCellQty", type: "NotBlank", msg: function(searchParams: SearchParams) {return (searchParams.skuType == "WB") && !searchParams.skuCellQty ? "SKU Cell을 선택해 주세요." : "";}},
  {id: "reqInfos", type: "NotFound", msg: "호출할 SKU가 없습니다."}
];

const validationMsg4pick = [
  {id: "workStationId", type: "NotBlank", msg: "작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."},
  {id: "skuReqType", type: "NotBlank", msg: "호출구분을 선택해 주세요."},
  {id: "skuReqType", type: "InvalidType", msg: "호출구분을 확인해 주세요."},
  {id: "procType", type: "NotBlank", msg: "처리구분을 선택해 주세요."},
  {id: "skuType", type: "NotBlank", msg: "SKU Type을 선택해 주세요."},
  {id: "skuSize", type: "NotBlank", msg: "SKU Size를 선택해 주세요."},
  {id: "skuCellQty", type: "NotBlank", msg: function(searchParams: SearchParams) {return (searchParams.skuType == "WB") && !searchParams.skuCellQty ? "SKU Cell을 선택해 주세요." : "";}},
  {id: "reqInfos", type: "NotFound", msg: "호출할 SKU가 없습니다."}
];

function PopupSkuReq(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/inoutbound/popup_sku_req";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Location", field: "locationId", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Empty Cell", field: "", width: 110, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        const qty = p.data.skuCellQty ?? 0;
        const useQty = p.data.skuCellUseQty ?? 0;
        return (Number(qty) - Number(useQty));
      }
    },
    { headerName: "Stock Rate", field: "stockRate", width: 110, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Stock Count", field: "stockQty", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Type", field: "skuType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuType") },
    { headerName: "SKU Size", field: "skuSize", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuSize") },
    { headerName: "SKU Cell", field: "skuCellQty", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
  });

  useEffect(() => {
    init();
  }, []);

  const WORK_STATION_SKU_TYPE: Record<string, string> = {
    F11: "WB", F12: "WB", F13: "WB", S21: "WB",
    F14: "WP", F15: "WP", S22: "WP", S23: "WP",
  };
  useEffect(() => {
    const curr = searchParamsRef.current;
    const skuType = WORK_STATION_SKU_TYPE[curr.workStationId];
    if (!skuType || curr.skuType === skuType) return;
    if (skuType === "WB") {
      updateSearchParams({ skuType, skuSize: "SS", skuCellQty: "4" });
    } else if (skuType === "WP") {
      updateSearchParams({ skuType, skuSize: "SM", skuCellQty: "" });
    }
  }, [searchParams.workStationId]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    try {
      const data = await kutil.sendGet("/api/inoutbound/" + props.data.path + ".data?mode=searchSkuCellQtyCodes", {}, {
        reqFunction: init
      });
      setState(prev => ({ ...prev, skuWbCellQtyCodes: data.skuWbCellQtyCodes }));
      return true;
    } catch(e) {
      console.log(e);
      return false;
    }
  }

  async function initParams() {
    updateSearchParams({ workStationId: props.data.workStationId });
  }

  async function closePopup() {
    props.onClose();
  }
 
  async function saveRange() {
    const isValid = await kutil.checkValidation(searchParams, validationMsg4range);
    if (isValid == false) {
      return;
    }

    if (await kutil.confirm("호출하시겠습니까?") == false) {
      return false;
    }

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams, ["procType"]);
      const data = await kutil.sendPost("/api/inoutbound/" + props.data.path + ".proc?mode=reqSkuRange", reqSearchParams, {
        validationMsg: validationMsg4range
      });
      if (reqSearchParams.skuQty === String(data.procQty)) {
        await kutil.alert(`호출하였습니다.`);
      } else {
        await kutil.alert(`SKU가 부족하여 일부 호출하였습니다.(${data.procQty}/${reqSearchParams.skuQty})`);
      }
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    const isValid = await kutil.checkValidation(searchParams, validationMsg4pick);
    if (isValid == false) {
      return;
    }

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams, ["sortOrder", "skuQty"]);
      const data = await kutil.sendGet("/api/inoutbound/" + props.data.path + ".data?mode=searchSku", reqSearchParams, {});
      for (const info of data.infos) {
        info.workStationId = searchParams.workStationId;
      }
      setResult({
        searchParams: reqSearchParams,
        infos: data.infos,
      });
      if (data.infos.length == 0) {
        await kutil.alert("SKU가 없습니다.");
      }
    } catch(e) {
      console.log(e);
    }
  }

  async function savePick() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("호출할 SKU를 선택해 주세요.");
      return;
    }

    if (await kutil.confirm("호출하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/" + props.data.path + ".proc?mode=reqSkuPick", selectedInfos, {
        validationMsg: validationMsg4pick
      });
      await kutil.alert("호출하였습니다.");
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{"& .MuiDialog-container": {"& .MuiPaper-root": {width: "100%", maxWidth: "1500px",},},}}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          범위지정
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 좌측 영역 */}
            <div className="left-area">
              {/* 항목 */}
              <div>
                <p>작업대</p>
                <div className="sg-item">
                  <Select
                    value={searchParams.workStationId}
                    onChange={(e) => {updateSearchParams({ workStationId: e.target.value })}}
                    displayEmpty
                  >
                    {(commonCode.workStationId as any[]).map((code) => (
                      <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div>
                <p>호출구분</p>
                <FormControl>
                  <RadioGroup
                    name="skuReqType"
                    value={searchParams.skuReqType}
                    onChange={(e) => {updateSearchParams({ skuReqType: e.target.value })}}
                  >
                    <FormControlLabel value="R" control={<Radio size="small" />} label="범위지정" />
                    <FormControlLabel value="P" control={<Radio size="small" />} label="지정출고" />
                  </RadioGroup>
                </FormControl>
              </div>
              {searchParams.skuReqType == "R" &&
              <div>
                <p>적재가능률</p>
                <FormControl>
                  <RadioGroup
                    name="sortOrder"
                    value={searchParams.sortOrder}
                    onChange={(e) => {updateSearchParams({ sortOrder: e.target.value })}}
                  >
                    <FormControlLabel value="stockRateAsc_random" control={<Radio size="small" />} label="높은순" />
                    <FormControlLabel value="stockRateDesc_random" control={<Radio size="small" />} label="낮은순" />
                    <FormControlLabel value="emptySku" control={<Radio size="small" />} label="공SKU" />
                  </RadioGroup>
                </FormControl>
              </div>
              }
              {searchParams.skuReqType == "P" &&
              <div>
                <p>처리구분</p>
                <FormControl>
                  <RadioGroup
                    name="procType"
                    value={searchParams.procType}
                    onChange={(e) => {updateSearchParams({ procType: e.target.value })}}
                  >
                    <FormControlLabel value="N" control={<Radio size="small" />} label="신규" />
                    <FormControlLabel value="A" control={<Radio size="small" />} label="추가" />
                  </RadioGroup>
                </FormControl>
              </div>
              }
              <div>
                <p>SKU Type</p>
                <FormControl disabled>
                  <RadioGroup
                    name="skuType"
                    value={searchParams.skuType}
                  >
                    {(commonCode.skuType as any[]).map((code) => (
                      <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
              <div>
                <p>SKU Size</p>
                <FormControl>
                  <RadioGroup
                    name="skuSize"
                    value={searchParams.skuSize}
                    onChange={(e) => {updateSearchParams({ skuSize: e.target.value })}}
                  >
                    {(commonCode.skuSize as any[]).map((code) => (
                      <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value}
                        sx={{ display: (
                          code.code == "SM"
                          || code.code == "MM"
                          || ((searchParams.skuType == "WB" && code.code == "SS"))
                          || ((searchParams.skuType == "WP" && code.code == "MB"))
                          ) ? "flex" : "none" }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
              {searchParams.skuType == "WB" &&
              <div>
                <p>SKU Cell</p>
                <FormControl>
                  <RadioGroup
                    name="skuCellQty"
                    value={searchParams.skuCellQty}
                    onChange={(e) => {updateSearchParams({ skuCellQty: e.target.value })}}
                  >
                    {(state.skuWbCellQtyCodes as any[] ).map((code) => (
                      <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
              }
              {searchParams.skuReqType == "R" &&
              <div>
                <p>SKU 수량</p>
                <div className="sg-item">
                  <div className="qty-stepper">
                    <button type="button" onClick={() => { updateSearchParams({ skuQty: (Number(searchParams.skuQty) < 2 ? "1" : (String(Number(searchParams.skuQty) - 1))) }) }}>
                      <img src={minusIcon} alt="감소" />
                    </button>

                    <input
                      type="text"
                      inputMode="numeric"
                      value={searchParams.skuQty}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, "");
                        updateSearchParams({ skuQty: v })
                      }}
                      onBlur={() => {
                        const n = Number(searchParams.skuQty);
                        if (!searchParams.skuQty || isNaN(n)) {
                          alert("수량을 입력해 주세요.");
                          updateSearchParams({ skuQty: "1" })
                          return;
                        }
                        updateSearchParams({ skuQty: String(n) })
                      }}
                    />

                    <button type="button" onClick={() => { updateSearchParams({ skuQty: String(Number(searchParams.skuQty) + 1) }) }}>
                      <img src={plusIcon} alt="증가" />
                    </button>
                  </div>
                </div>
              </div>
              }
              {/* 구분선 */}
              {searchParams.skuType == "WB" && <em></em>}
              {/* 즐겨찾기 버튼 */}
              {searchParams.skuType == "WB" &&
              <span>
                <Button variant="additional" onClick={() => {updateSearchParams({ skuSize: "SS", skuCellQty: "4" })}}>초소형 4셀</Button>
                  <Button variant="additional" onClick={() => {updateSearchParams({ skuSize: "SS", skuCellQty: "6" })}}>초소형 6셀</Button>
                  <Button variant="additional" onClick={() => {updateSearchParams({ skuSize: "SM", skuCellQty: "2" })}}>소형 2셀</Button>
                  <Button variant="additional" onClick={() => {updateSearchParams({ skuSize: "MM", skuCellQty: "10" })}}>중형 10셀</Button>
              </span>
              }
              {/* 구분선 */}
              <em></em>
              {/* 조회 버튼 */}
              {searchParams.skuReqType == "P" &&
              <p>
                <Button variant="outlined" onClick={doSearch}>조회</Button>
              </p>
              }
            </div>
            {/*// 좌측 영역 */}
            {/* 우측 영역 */}
            <div className="right-area">
              {searchParams.skuReqType == "P" ? (
                <AgGridProvider modules={modules}>
                  <div className="grid-area">
                    <CustomAgGridReact
                      columnDefs={gridInfo}
                      rowData={result.infos}
                      defaultColDef={kutil.defaultColDef}
                      initialState={gridState}
                      rowSelection={gridOptions.rowSelection}
                      selectionColumnDef={gridOptions.selectionColumnDef}
                      onGridReady={gridCallbacks.onGridReady}
                      onStateUpdated={gridCallbacks.onStateUpdated}
                    />
                  </div>
                </AgGridProvider>
              ) : (
                <div className="empty-guide">
                  <img src={messageIcon} alt="" />
                  <p>조건을 설정한 후 <strong>호출</strong> 버튼을 눌러주세요.</p>
                  <p>지정출고 시에는 조건 설정 및 조회 후 호출할 SKU를 선택해서 호출해 주세요.</p>
                </div>
              )}
            </div>
            {/*// 우측 영역 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            취소
          </Button>
          {searchParams.skuReqType == "R" && auth.write &&
          <Button variant="contained" autoFocus onClick={saveRange}>
            호출
          </Button>
          }
          {searchParams.skuReqType == "P" && auth.write &&
          <Button variant="contained" autoFocus onClick={savePick}>
            호출
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupSkuReq);
